/**
 * Entities Selectors
 * Creating Memoized Selectors
 */

import { createSelector } from '@reduxjs/toolkit';

import { getSelectedStoresIds, selectIsTaxIncluded } from '../Preferences/selectors';
import { selectStores } from '../Stores/selectors';

import {
  getReferenceDataKpiOfSelectedStores as getReferenceAverageSpents,
  getSelectedDataKpiOfSelectedStores as getSelectedAverageSpents,
} from './AverageSpentsAggregated/selectors';
import {
  getReferenceDataKpiOfSelectedStores as getReferenceCustomersCounts,
  getSelectedDataKpiOfSelectedStores as getSelectedCustomersCounts,
} from './CustomersCountsAggregated/selectors';
import {
  getReferenceDataKpiOfSelectedStores as getReferenceRevenues,
  getSelectedDataKpiOfSelectedStores as getSelectedRevenues,
} from './RevenuesAggregated/selectors';
import { getEvolution } from './helpers';

/**
 * Get all stores KPIs used to hydrate table below the graph on the Dashboard page.
 * Check PerStoreTable component to see use case of that method.
 *
 * @return {Object[]} array of { name, customersCount: {evolution, selected}, averageSpent: {evolution, selected}, taxExcluded: {evolution, selected}, taxIncluded: {evolution, selected}}
 */
export const getKpisPerStore = createSelector(
  [
    getSelectedAverageSpents,
    getReferenceAverageSpents,
    getSelectedCustomersCounts,
    getReferenceCustomersCounts,
    getSelectedRevenues,
    getReferenceRevenues,
    selectStores,
    getSelectedStoresIds,
    selectIsTaxIncluded,
  ],
  (
    selectedAverageSpents,
    referenceAverageSpents,
    selectedCustomersCounts,
    referenceCustomersCounts,
    selectedRevenues,
    referenceRevenues,
    stores,
    storeIds,
    isTaxIncl,
  ) => {
    if (
      selectedAverageSpents.length &&
      referenceAverageSpents.length &&
      referenceCustomersCounts.length &&
      selectedCustomersCounts.length &&
      referenceRevenues.length &&
      selectedRevenues.length &&
      stores.length &&
      storeIds.length
    ) {
      const taxKey = isTaxIncl ? 'tax_included' : 'tax_excluded';

      return storeIds.map(storeId => {
        const { name } = stores.find(({ id }) => id === storeId);
        const selectedCustomersCount =
          selectedCustomersCounts.find(({ id }) => parseInt(id, 10) === storeId)?.value
            .customersCount ?? 0;
        const referenceCustomersCount =
          referenceCustomersCounts.find(({ id }) => parseInt(id, 10) === storeId)?.value
            .customersCount ?? 0;
        const selectedAverageSpent =
          selectedAverageSpents.find(({ id }) => parseInt(id, 10) === storeId)?.value[taxKey] ?? 0;
        const referenceAverageSpent =
          referenceAverageSpents.find(({ id }) => parseInt(id, 10) === storeId)?.value[taxKey] ?? 0;
        const selectedRevenue =
          selectedRevenues.find(({ id }) => parseInt(id, 10) === storeId)?.value ?? 0;
        const referenceRevenue =
          referenceRevenues.find(({ id }) => parseInt(id, 10) === storeId)?.value ?? 0;

        return {
          name,
          customersCount: {
            selected: selectedCustomersCount,
            evolution: getEvolution(selectedCustomersCount, referenceCustomersCount),
          },
          averageSpent: {
            selected: selectedAverageSpent,
            evolution: getEvolution(selectedAverageSpent, referenceAverageSpent),
          },
          taxExcluded: {
            selected: selectedRevenue.tax_excluded,
            evolution: getEvolution(selectedRevenue.tax_excluded, referenceRevenue.tax_excluded),
          },
          taxIncluded: {
            selected: selectedRevenue.tax_included,
            evolution: getEvolution(selectedRevenue.tax_included, referenceRevenue.tax_included),
          },
        };
      });
    }

    return [];
  },
);
